import React from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Edit3, Lock, LogOut, Menu, Trash2, X } from "lucide-react";
import { navItems, useHeader, type HeaderVariant } from "./useHeader";
import "./Header.scss";

type HeaderProps = {
  variant?: HeaderVariant;
  stickyBelow?: React.ReactNode;
};

const Header: React.FC<HeaderProps> = ({ variant = "default", stickyBelow }) => {
  const {
    user,
    location,
    showPublicHeader,
    profileDropdownOpen,
    setProfileDropdownOpen,
    mobileMenuOpen,
    setMobileMenuOpen,
    scrolled,
    scrollProgress,
    fixedStackHeight,
    profileDropdownRef,
    fixedStackRef,
    isActiveRoute,
    getNavButtonClasses,
    handleLogout,
    handleNavigation,
  } = useHeader(variant, Boolean(stickyBelow));

  const headerBarClasses = `header-bar ${scrolled ? "is-scrolled" : "is-idle"}`;

  const headerBar = (
    <header className={headerBarClasses}>
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        <div className="flex items-center justify-between gap-3 py-4 sm:py-5">
          <Link to="/" className="flex min-w-0 items-center space-x-2 sm:space-x-3">
            <div className="header-logo-tile flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-full text-accent-300 sm:h-10 sm:w-10">
              <img
                src={`${process.env.PUBLIC_URL}/EOS_image.png`}
                alt="Engineering of Stocks"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="min-w-0">
              <h1 className="text-base font-bold text-slate-100 sm:text-lg lg:text-xl">Engineering of Stocks</h1>
              <p className="text-xs text-slate-400">Trading Intelligence</p>
            </div>
          </Link>

          {showPublicHeader ? (
            <Link
              to={location.pathname === "/login" ? "/" : "/login"}
              className="relative inline-flex flex-shrink-0 items-center justify-center whitespace-nowrap rounded-full bg-gradient-to-r from-accent-500 to-emerald-500 px-3 py-1.5 text-xs font-semibold text-ink-950 shadow-[0_0_30px_-5px_rgba(56,189,248,0.6)] transition-all duration-300 hover:shadow-[0_0_45px_-5px_rgba(56,189,248,0.85)] hover:scale-[1.03] sm:px-4 sm:py-2 sm:text-sm"
            >
              {location.pathname === "/login" ? "Back to Home" : "Login"}
            </Link>
          ) : (
            <>
              <div className="hidden md:flex items-center space-x-4 lg:space-x-6">
                {navItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => handleNavigation(item.path)}
                    className={getNavButtonClasses(item.path)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center space-x-2">
                <div className="hidden md:block relative profile-dropdown" ref={profileDropdownRef}>
                  <button
                    onClick={() => setProfileDropdownOpen((prev) => !prev)}
                    className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-slate-300 hover:text-accent-300 hover:bg-white/5 rounded-lg transition-all duration-200"
                  >
                    <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center">
                      <span className="text-sm font-semibold text-accent-300">{user?.firstName?.charAt(0) || "U"}</span>
                    </div>
                    <span>{user?.firstName || "User"}</span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform duration-200 ${profileDropdownOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {profileDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 rounded-lg border border-white/10 bg-ink-950/95 backdrop-blur-md py-2 shadow-xl z-50">
                      <button className="w-full flex items-center px-4 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-accent-300 transition-colors duration-200">
                        <Edit3 className="h-4 w-4 mr-3" />
                        Edit Profile
                      </button>
                      <button className="w-full flex items-center px-4 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-accent-300 transition-colors duration-200">
                        <Lock className="h-4 w-4 mr-3" />
                        Change Password
                      </button>
                      <button className="w-full flex items-center px-4 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-accent-300 transition-colors duration-200">
                        <Trash2 className="h-4 w-4 mr-3" />
                        Delete Account
                      </button>
                      <div className="border-t border-white/10 my-1" />
                      <button
                        onClick={() => {
                          setProfileDropdownOpen(false);
                          handleLogout();
                        }}
                        className="w-full flex items-center px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors duration-200"
                      >
                        <LogOut className="h-4 w-4 mr-3" />
                        Logout
                      </button>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setMobileMenuOpen((prev) => !prev)}
                  className="md:hidden p-2 text-slate-300 hover:text-accent-300 hover:bg-white/5 rounded-lg transition-all duration-200"
                >
                  {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
              </div>
            </>
          )}
        </div>

        {mobileMenuOpen && !showPublicHeader && (
          <div className="md:hidden border-t border-white/10 bg-ink-950/95 backdrop-blur-md">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  className={`w-full text-left px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                    isActiveRoute(item.path)
                      ? "bg-white/10 text-accent-300"
                      : "text-slate-300 hover:bg-white/5 hover:text-accent-300"
                  }`}
                >
                  {item.label}
                </button>
              ))}

              <div className="border-t border-white/10 pt-3 mt-3">
                <div className="flex items-center px-3 py-2">
                  <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center mr-3">
                    <span className="text-sm font-semibold text-accent-300">{user?.firstName?.charAt(0) || "U"}</span>
                  </div>
                  <span className="text-sm font-medium text-slate-300">{user?.firstName || "User"}</span>
                </div>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleLogout();
                  }}
                  className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors duration-200"
                >
                  <LogOut className="h-4 w-4 inline mr-3" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );

  return (
    <>
      <div
        className="fixed top-0 left-0 z-[60] h-[2px] origin-left bg-gradient-to-r from-accent-400 via-accent-glow to-emerald-400"
        style={{ width: `${scrollProgress}%` }}
      />
      <div style={{ height: fixedStackHeight }} aria-hidden="true" />
      <div ref={fixedStackRef} className="fixed top-0 inset-x-0 z-50">
        {headerBar}
        {stickyBelow}
      </div>
    </>
  );
};

export default Header;
